import React from 'react';
import { Helmet } from 'react-helmet-async';
import { GraduationCap, Clock, Users, ExternalLink, TrendingUp, Building, Brain, FileText, BarChart3, Calculator, BookOpen } from 'lucide-react';

const Courses = () => {
  const courseCategories = [
    {
      title: '🇨🇦 Canadian Securities & Regulatory Exams',
      description: 'Comprehensive exam preparation for IFC and CSC certifications',
      courses: [
        {
          title: 'Investment Funds in Canada (IFC/IFIC) Tutorials',
          description: 'Complete IFC exam preparation with video tutorials covering all chapters.',
          icon: GraduationCap,
          link: 'https://www.udemy.com/course/investment-funds-in-canada-ifc-ific-tutorials/?couponCode=FEB-DEAL',
        },
        {
          title: 'Investment Funds in Canada (IFC/IFIC) Question Bank',
          description: 'Extensive question bank to practice for your IFC exam.',
          icon: FileText,
          link: 'https://www.udemy.com/course/investment-funds-in-canada-ifc-ific-question-bank/?couponCode=FEB-DEAL',
        },
        {
          title: 'Investment Funds in Canada (IFIC) - 6 Full-length Mock Exams',
          description: 'Six complete mock exams to test your IFC exam readiness.',
          icon: FileText,
          link: 'https://www.udemy.com/course/investment-funds-in-canada-ific-6-full-length-mock-exams/?couponCode=FEB-DEAL',
        },
        {
          title: 'Canadian Securities Course (CSC) Tutorials (Level 1 and 2)',
          description: 'Complete CSC exam preparation covering both levels.',
          icon: GraduationCap,
          link: 'https://www.udemy.com/course/prep-for-canadian-securities-course-csc/?couponCode=FEB-DEAL',
        },
        {
          title: 'Canadian Securities Course (CSC) Level 1 Question Bank',
          description: 'Practice questions for CSC Level 1 exam preparation.',
          icon: FileText,
          link: 'https://www.udemy.com/course/canadian-securities-course-csc-level-1-question-bank-2023/?couponCode=FEB-DEAL',
        },
        {
          title: 'Canadian Securities Course (CSC) Level 2 Question Bank',
          description: 'Practice questions for CSC Level 2 exam preparation.',
          icon: FileText,
          link: 'https://www.udemy.com/course/canadian-securities-course-csc-level-2-question-bank-2023/?couponCode=FEB-DEAL',
        },
      ],
    },
    {
      title: '🏠 Investing and Real Estate in Canada',
      description: 'Comprehensive guides to building wealth through Canadian investments',
      courses: [
        {
          title: 'Investing in Canada: A Complete Guide to Making Investments',
          description: 'Comprehensive Canadian investing guide covering stocks, ETFs, and more.',
          icon: TrendingUp,
          link: 'https://www.udemy.com/course/investing-in-canada-a-complete-guide-to-making-investmnets/?couponCode=FEB-DEAL',
        },
        {
          title: 'Real Estate Investing in Canada Master Class',
          description: 'Complete guide to Canadian real estate investment strategies.',
          icon: Building,
          link: 'https://www.udemy.com/course/real-estate-investing-h/?couponCode=FEB-DEAL',
        },
        {
          title: 'Financial Planning Tutorials',
          description: 'Master Canadian financial planning, TFSA, RRSP, and retirement strategies.',
          icon: BookOpen,
          link: 'https://www.udemy.com/course/financial-planning-tutorials-canada/?couponCode=FEB-DEAL',
        },
      ],
    },
    {
      title: '🤖 AI-Powered Finance & Advanced Analysis',
      description: 'Modern finance courses leveraging AI for financial modeling and analysis',
      courses: [
        {
          title: 'AI-Powered Canadian Corporate Finance & Financial Modelling',
          description: 'Learn corporate finance with AI-powered financial modeling techniques.',
          icon: Brain,
          link: 'https://www.udemy.com/course/canadian-corporate-finance/?couponCode=FEB-DEAL',
        },
        {
          title: 'AI-Powered Financial Ratio, Modeling & Technical Trading',
          description: 'Master financial ratios and technical trading with AI assistance.',
          icon: BarChart3,
          link: 'https://www.udemy.com/course/financial-ratios-simplified-a-practicle-guide/?couponCode=FEB-DEAL',
        },
        {
          title: 'Financial Risk Management (FRM) / CFA - Derivatives Tutorial',
          description: 'Detailed derivatives preparation for FRM and CFA exams.',
          icon: Calculator,
          link: 'https://www.udemy.com/course/cfa-derivatives-a-detailed-preparation-guide/?couponCode=FEB-DEAL',
        },
        {
          title: 'CFA Equity and Portfolio Management Practical Tutorials',
          description: 'Practical equity analysis and portfolio management for CFA candidates.',
          icon: TrendingUp,
          link: 'https://www.udemy.com/course/cfa-equity-and-portfolio-manangement-practical-tutorials/?couponCode=FEB-DEAL',
        },
      ],
    },
  ];

  // Count total courses
  const totalCourses = courseCategories.reduce((sum, cat) => sum + cat.courses.length, 0);

  // Structured data for all courses
  const coursesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Canadian Investing and Financial Courses",
    "description": "Comprehensive online courses for Canadian investing, securities licensing, and financial planning",
    "numberOfItems": totalCourses,
    "itemListElement": courseCategories.flatMap((category, catIndex) => 
      category.courses.map((course, courseIndex) => ({
        "@type": "Course",
        "position": catIndex * 20 + courseIndex + 1,
        "name": course.title,
        "description": course.description,
        "provider": {
          "@type": "Person",
          "name": "Harry Vadalkar"
        },
        "educationalLevel": "Beginner to Advanced"
      }))
    )
  };

  return (
    <div className="min-h-screen" data-testid="courses-page">
      <Helmet>
        <title>Canadian Investing Courses | IFC Exam Prep | CSC Course | CIRO Mock Exams | CFA Prep | Real Estate | Udemy | Harry Vadalkar</title>
        <meta name="description" content="13+ comprehensive Canadian investing courses on Udemy. IFC exam prep tutorials and question banks, CSC course for levels 1 and 2, CFA derivatives, real estate investing masterclass, AI-powered financial modeling, and financial planning. Expert instruction from Harvard & MIT certified Harry Vadalkar. Join 30,000+ successful students." />
        <meta name="keywords" content="IFC exam prep, IFC exam preparation course, IFC question bank, IFC mock exams, IFC tutorials, IFIC exam, Investment Funds in Canada course, CSC course, CSC exam prep, CSC level 1 question bank, CSC level 2 question bank, Canadian Securities Course tutorials, Canadian investing courses online, real estate investing Canada course, CFA derivatives course, CFA prep, CFA equity, FRM course, financial modeling course, AI finance course, AI financial modeling, Harry Vadalkar Udemy, Canadian securities exam prep, financial planning course Canada, TFSA course, RRSP course, mutual fund course Canada, best Udemy finance courses" />
        <link rel="canonical" href="https://harryvadalkar.ca/courses" />
        <meta property="og:title" content="13+ Canadian Investing Courses | IFC, CSC Exam Prep" />
        <meta property="og:description" content="Expert Canadian investing courses on Udemy. IFC, CSC, CIRO exam prep with tutorials, question banks, and mock exams." />
        <script type="application/ld+json">
          {JSON.stringify(coursesSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-[#020617]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full mb-6">
            <GraduationCap className="w-4 h-4" />
            <span className="font-medium">{totalCourses}+ Courses on Udemy</span>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
            All <span className="text-blue-400">Udemy Courses</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Comprehensive courses on Canadian securities exams, AI-powered finance, and investing. From IFC & CSC prep to real estate and financial planning.
          </p>
        </div>
      </section>

      {/* Course Categories */}
      {courseCategories.map((category, catIndex) => (
        <section 
          key={catIndex} 
          className={`py-16 ${catIndex % 2 === 1 ? 'bg-slate-900/30' : ''}`}
          data-testid={`course-category-${catIndex}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
                {category.title}
              </h2>
              <p className="text-slate-400 text-lg">{category.description}</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.courses.map((course, courseIndex) => (
                <div
                  key={courseIndex}
                  className="glass rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300 flex flex-col"
                >
                  <course.icon className="w-8 h-8 text-blue-400 mb-4" />
                  <h3 className="font-semibold text-lg text-white mb-2 line-clamp-2">
                    {course.title}
                  </h3>
                  <p className="text-slate-400 text-sm mb-6 flex-grow">{course.description}</p>
                  
                  <a
                    href={course.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`course-link-${catIndex}-${courseIndex}`}
                    className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-3 rounded-lg transition-all text-sm"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Enroll on Udemy
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-20" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to <span className="text-blue-400">Start Learning</span>?
          </h2>
          <p className="text-slate-300 text-lg mb-4">
            Browse all {totalCourses}+ courses on Udemy and start your journey to financial expertise.
          </p>
          <p className="text-slate-400 mb-8">
            Questions? Contact us at{' '}
            <a href="mailto:harryvadalkar@gmail.com" className="text-blue-400 hover:text-blue-300">
              harryvadalkar@gmail.com
            </a>
          </p>
          <a
            href="https://www.udemy.com/user/heramb-12/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="udemy-profile-link"
            className="inline-flex items-center bg-purple-600 hover:bg-purple-700 text-white font-bold px-10 py-4 rounded-full transition-all shadow-lg text-lg"
          >
            <ExternalLink className="w-5 h-5 mr-2" />
            View All on Udemy
          </a>
        </div>
      </section>
    </div>
  );
};

export default Courses;
